import { useCallback } from 'react';

import { type BuildQuizListItem } from '../../../form/schema/BuildQuizListSchema';
import { createAiBuilderUserMessageId } from '../store/aiBuilderMessageState';
import {
  createAiBuilderClientRequestId,
  createUndoTurnRequest,
} from '../store/aiBuilderTurnRequests';
import { isAiBuilderUsageBlocked } from '../store/aiBuilderUsagePolicy';
import {
  type AiBuilderChatState,
  type AiBuilderSelectedQuizContext,
  type AiBuilderSubmitPromptOptions,
  type AiBuilderUserMessageRetryRequest,
} from '../store/types';
import { type useDispatchServerTurn } from '../store/useAiBuilderServerTurnDispatcher';
import { aiBuilderChatAdapter } from '../utils/aiBuilderChatAdapter';
import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { trackAiBuilderUsageBlocked } from '../utils/aiBuilderChatTracking';
import { type AiBuilderTurnRequest } from '../utils/types';

type AiBuilderMutableRef<T> = { current: T };
type GetNextAiBuilderRequestId = () => number;
type IsAiBuilderTurnBusy = () => boolean;
type CreateTurnContextParams = {
  signal?: AbortSignal;
  selectedQuizContext?: AiBuilderSelectedQuizContext | null;
  sourceRefs?: string[];
  turnHints?: AiBuilderSubmitPromptOptions['turnHints'];
};

type UseAiBuilderSubmitUndoLastAiActionParams = {
  appendUserMessage: (params: {
    content: string;
    requestId: number;
    retry?: AiBuilderUserMessageRetryRequest;
  }) => void;
  copy: AiBuilderChatCopy;
  createTurnContext: (
    params?: CreateTurnContextParams,
  ) => AiBuilderTurnRequest['context'];
  dispatchServerTurn: ReturnType<typeof useDispatchServerTurn>;
  getChatState: () => AiBuilderChatState;
  getNextRequestId: GetNextAiBuilderRequestId;
  isTurnBusy: IsAiBuilderTurnBusy;
  lastQuizListSnapshotRef: AiBuilderMutableRef<BuildQuizListItem[] | null>;
};

export const useAiBuilderSubmitUndoLastAiAction = ({
  appendUserMessage,
  copy,
  createTurnContext,
  dispatchServerTurn,
  getChatState,
  getNextRequestId,
  isTurnBusy,
  lastQuizListSnapshotRef,
}: UseAiBuilderSubmitUndoLastAiActionParams) => {
  return useCallback(
    (prompt: string) => {
      const trimmedPrompt = prompt.trim();

      if (!trimmedPrompt || isTurnBusy()) {
        return;
      }

      const usage = getChatState().usage;
      if (isAiBuilderUsageBlocked(usage)) {
        trackAiBuilderUsageBlocked(usage, 'undo');
        return;
      }

      const requestId = getNextRequestId();
      const clientRequestId = createAiBuilderClientRequestId();
      const userMessageId = createAiBuilderUserMessageId(requestId);

      appendUserMessage({
        content: trimmedPrompt,
        requestId,
        retry: { clientRequestId, type: 'undo' },
      });

      lastQuizListSnapshotRef.current = null;
      dispatchServerTurn(
        aiBuilderChatAdapter.startTurn,
        signal =>
          createUndoTurnRequest({
            requestId,
            clientRequestId,
            clientCopy: copy,
            context: createTurnContext({ signal }),
          }),
        { userMessageId },
      );
    },
    [
      appendUserMessage,
      copy,
      createTurnContext,
      dispatchServerTurn,
      getChatState,
      getNextRequestId,
      isTurnBusy,
      lastQuizListSnapshotRef,
    ],
  );
};
